import React, { useState, useEffect } from 'react';
import { supabase } from '../../../utils/supabaseClient';

interface Team {
    id: number;
    name: string;
}

interface Persona {
    id: number;
    first_name: string;
    last_name: string;
    inSeason: boolean;
    inTournament: boolean;
}

interface Persona2Team {
    fk_persona: number;
    personas: {
        id: number;
        first_name: string;
        last_name: string;
    };
    persona2team2season: {
        id: number;
        persona2team2season2tournament: {
            id: number;
        }[];
    }[];
}

interface Season {
    id: number;
    name: string;
}

interface Tournament {
    id: number;
    name: string;
}

const AdminPersonaMove: React.FC = () => {
    const [teams, setTeams] = useState<Team[]>([]);
    const [personas, setPersonas] = useState<Persona[]>([]);
    const [seasons, setSeasons] = useState<Season[]>([]);
    const [tournaments, setTournaments] = useState<Tournament[]>([]);

    const [selectedTeam, setSelectedTeam] = useState<number | null>(null);
    const [selectedPersonas, setSelectedPersonas] = useState<number[]>([]);
    const [selectedSeason, setSelectedSeason] = useState<number | null>(null);
    const [selectedTournament, setSelectedTournament] = useState<number | null>(null);
    const [selectAll, setSelectAll] = useState(false);
    const [targetTable, setTargetTable] = useState<string>('persona2team2season');

    useEffect(() => {
        fetchTeams();
        fetchSeasons();
        fetchTournaments();
    }, []);

    const fetchTeams = async () => {
        const { data, error } = await supabase.from('teams').select('*');
        if (error) {
            console.error('Error fetching teams:', error);
        } else {
            setTeams(data);
        }
    };
    const fetchPersonas = async (teamId: number) => {
        const { data, error } = await supabase
            .from('persona2team')
            .select(`
            fk_persona,
            personas (
                id,
                first_name,
                last_name
            ),
            persona2team2season (
                id,
                persona2team2season2tournament (
                    id
                )
            )
        `)
            .eq('fk_team', teamId);

        if (error) {
            console.error('Error fetching personas:', error.message);
            return;
        }

        if (data) {
            const formattedPersonas: Persona[] = data.map(item => {
                const persona = item.personas as unknown as { id: number; first_name: string; last_name: string };
                if (!persona) {
                    console.error('Error: personas is undefined or not an object', item);
                    return null;
                }

                return {
                    id: persona.id,
                    first_name: persona.first_name,
                    last_name: persona.last_name,
                    inSeason: item.persona2team2season.length > 0,
                    inTournament: item.persona2team2season.some(season => season.persona2team2season2tournament.length > 0),
                };
            }).filter(persona => persona !== null) as Persona[];

            setPersonas(formattedPersonas);
        }
    };


    const fetchSeasons = async () => {
        const { data, error } = await supabase.from('seasons').select('*');
        if (error) {
            console.error('Error fetching seasons:', error);
        } else {
            setSeasons(data);
        }
    };

    const fetchTournaments = async () => {
        const { data, error } = await supabase.from('tournaments').select('*');
        if (error) {
            console.error('Error fetching tournaments:', error);
        } else {
            setTournaments(data);
        }
    };

    const handleTeamChange = (teamId: number) => {
        setSelectedTeam(teamId);
        fetchPersonas(teamId);
        setSelectedPersonas([]);
        setSelectAll(false);
    };

    const handlePersonaChange = (personaId: number) => {
        setSelectedPersonas(prev =>
            prev.includes(personaId)
                ? prev.filter(id => id !== personaId)
                : [...prev, personaId]
        );
    };

    const handleSelectAllChange = () => {
        setSelectAll(!selectAll);
        setSelectedPersonas(selectAll ? [] : personas.map(p => p.id));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedTeam || selectedPersonas.length === 0) {
            alert('Please select a team and at least one player');
            return;
        }

        try {
            for (const personaId of selectedPersonas) {
                if (targetTable === 'persona2team2season' || targetTable === 'persona2team2season2tournament') {
                    if (!selectedSeason) {
                        alert('Please select a season');
                        return;
                    }
                    const { data: p2t2sData, error: p2t2sError } = await supabase
                        .from('persona2team2season')
                        .insert({ fk_persona2team: personaId, fk_season: selectedSeason })
                        .select();

                    if (p2t2sError) throw p2t2sError;

                    if (targetTable === 'persona2team2season2tournament') {
                        if (!selectedTournament) {
                            alert('Please select a tournament');
                            return;
                        }
                        const { error: p2t2s2tError } = await supabase
                            .from('persona2team2season2tournament')
                            .insert({
                                fk_persona2team2season: p2t2sData[0].id,
                                fk_tournament: selectedTournament,
                            });

                        if (p2t2s2tError) throw p2t2s2tError;
                    }
                }
            }

            alert(`Successfully moved personas to ${targetTable}!`);
        } catch (error) {
            console.error('Error moving personas:', error);
            alert('An error occurred while moving the personas. Please check the console for details.');
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <select onChange={(e) => handleTeamChange(Number(e.target.value))}>
                <option value="">Select Team</option>
                {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                ))}
            </select>

            {selectedTeam && (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label>
                        <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAllChange}
                        />
                        Select All
                    </label>
                    {personas.map(persona => (
                        <label key={persona.id} style={{ marginLeft: '20px' }}>
                            <input
                                type="checkbox"
                                checked={selectedPersonas.includes(persona.id)}
                                onChange={() => handlePersonaChange(persona.id)}
                            />
                            {persona.first_name} {persona.last_name}
                            {persona.inSeason && ' (In Season)'}
                            {persona.inTournament && ' (In Tournament)'}
                        </label>
                    ))}
                </div>
            )}

            <select onChange={(e) => setTargetTable(e.target.value)}>
                <option value="persona2team2season">persona2team2season</option>
                <option value="persona2team2season2tournament">persona2team2season2tournament</option>
            </select>

            <select onChange={(e) => setSelectedSeason(Number(e.target.value))}>
                <option value="">Select Season</option>
                {seasons.map(season => (
                    <option key={season.id} value={season.id}>{season.name}</option>
                ))}
            </select>

            {targetTable === 'persona2team2season2tournament' && (
                <select onChange={(e) => setSelectedTournament(Number(e.target.value))}>
                    <option value="">Select Tournament</option>
                    {tournaments.map(tournament => (
                        <option key={tournament.id} value={tournament.id}>{tournament.name}</option>
                    ))}
                </select>
            )}

            <button type="submit">Move Personas</button>
        </form>
    );
};

export default AdminPersonaMove;
